import React, { useState, useEffect } from 'react';
import usePersistedTab from '../hooks/usePersistedTab';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Grid,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Save,
  RestartAlt,
  Refresh
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { formatSettingValue, parseSettingValue } from '../utils/settings';
import axios from 'axios';

const TabPanel = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const Settings = () => {
  const [tabValue, setTabValue] = usePersistedTab('settings', 0);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [lastRateUpdate, setLastRateUpdate] = useState(null);

  // Начальные значения настроек согласно ТЗ
  const defaultSettings = {
    // CLAIM настройки
    claim_cooldown_hours: 24,
    claim_hold_seconds: 3,
    claim_auto_collect_on_stake: true,

    // Включение/выключение стейкингов
    lunar_staking_enabled: true,
    star_staking_enabled: false,
    lunar_staking_message: 'В данный момент недоступно',
    star_staking_message: 'Скоро открытие! 🚀',
    
    // Лунный стейкинг (вывод тела невозможен - вечная регрессия)
    lunar_initial_apr: 1.0,
    lunar_daily_growth: 0.05,
    lunar_max_apr: 5.0,
    lunar_min_deposit: 50,
    lunar_burn_percent: 10,
    lunar_visual_multiplier: 2,

    // Звездный стейкинг (вывод со штрафом)
    star_initial_apr: 0.1,
    star_daily_growth: 0.01,
    star_min_deposit: 50,
    star_burn_percent: 5,
    star_penalty_30days: 20,
    star_penalty_100days: 10,
    star_penalty_200days: 5,
    star_penalty_over200: 0,

    // 🌙 Лунный марафон (USDT)
    marathon_enabled: true,
    marathon_min_deposit_usdt: 100,
    marathon_period_days: 14,
    marathon_total_periods: 20,
    marathon_daily_apr: 0.7,
    marathon_total_roi: 254,
    marathon_message: 'Лунный марафон - стейкинг в USDT',

    // Включение/выключение мини-игр
    game_moon_chase_enabled: true,
    game_moon_catcher_enabled: true,
    game_speed_click_enabled: true,
    game_color_match_enabled: true,
    game_lucky_number_enabled: true,
    games_disabled_message: 'Игры временно недоступны',
    
    // Мини-игры (бусты)
    minigames_enabled: true,
    lottery_boost_percent: 10,
    lottery_boost_minutes: 5,
    lottery_cooldown_hours: 24,
    flip_boost_percent: 15,
    flip_boost_minutes: 3,
    flip_cooldown_hours: 12,
    dice_boost_percent: 20,
    dice_boost_minutes: 2,
    dice_cooldown_hours: 8,
    race_boost_percent: 25,
    race_boost_minutes: 1,
    race_cooldown_hours: 6,
    mining_boost_percent: 30,
    mining_boost_minutes: 1,
    mining_cooldown_hours: 4,
    
    // Реферальная система - единовременные выплаты (% от первого депозита)
    referral_enabled: true,
    ref_level1_deposit_percent: 5,
    ref_level2_deposit_percent: 2.5,
    ref_level3_deposit_percent: 1.5,
    ref_level4_deposit_percent: 1,
    ref_level5_deposit_percent: 0.5,
    
    // Реферальная система - ежедневный процент от дохода
    ref_level1_daily_percent: 10,
    ref_level2_daily_percent: 5,
    ref_level3_daily_percent: 3,
    ref_level4_daily_percent: 2,
    ref_level5_daily_percent: 1,
    
    // VIP статусы (за инвестиции)
    vip_bronze_threshold: 10000,
    vip_bronze_apr_bonus: 0.1,
    vip_silver_threshold: 100000,
    vip_silver_apr_bonus: 0.2,
    vip_gold_threshold: 1000000,
    vip_gold_apr_bonus: 0.3,
    vip_platinum_threshold: 10000000,
    vip_platinum_apr_bonus: 0.5,
    
    // Лидерские статусы (за рефералов)
    leader_bronze_refs: 3,
    leader_bronze_apr_bonus: 0.05,
    leader_silver_refs: 10,
    leader_silver_apr_bonus: 0.1,
    leader_gold_refs: 30,
    leader_gold_apr_bonus: 0.2,
    leader_platinum_refs: 50,
    leader_platinum_apr_bonus: 0.3,
    leader_diamond_refs: 100,
    leader_diamond_apr_bonus: 0.5,
    
    // Ранги (за регулярность)
    rank_bronze_days: 7,
    rank_silver_days: 14,
    rank_gold_days: 30,
    rank_platinum_days: 60,
    
    // Общие настройки
    maintenance_mode: false,
    maintenance_message: 'Ведутся технические работы. Следите за нашим телеграм-каналом. Спасибо за понимание!',
    new_registrations: true,
    min_withdrawal: 100,
    withdrawal_fee_percent: 2,
    max_withdrawal_per_day: 100000,
    
    // Безопасность
    max_login_attempts: 5,
    lockout_duration_minutes: 30,
    session_duration_hours: 24,
    
    // Уведомления
    telegram_notifications: true,
    email_notifications: false,
    push_notifications: true,
    
    // Депозиты и выводы
    min_deposit_amount: 100,
    max_deposit_amount: 1000000,
    exchange_rate: 1000,
    deposit_wallet_address: 'UQD3gDhLN0vF_aF5A9Bak5JVLaYGqVB5wRgB6f7UQQkVmtPb',
    min_withdrawal_amount: 50,
    withdrawal_commission: 0,
    max_daily_withdrawal: 100000,
    withdrawal_processing_time: 24,
    auto_withdrawal_enabled: false,
    withdrawal_notifications: true,
    min_deposit_for_withdrawal: 200,
    
  };

  useEffect(() => {
    loadSettings();
    loadExchangeRate();
    
    // Обновляем курс каждые 20 секунд
    const interval = setInterval(loadExchangeRate, 20000);
    return () => clearInterval(interval);
  }, []);

  const loadExchangeRate = async () => {
    try {
      setRateLoading(true);
      const response = await axios.get('/api/admin/exchange-rate');
      setExchangeRate(response.data.rate);
      setLastRateUpdate(new Date());
    } catch (error) {
      console.error('Ошибка загрузки курса:', error);
    } finally {
      setRateLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSettings();
      if (response.data.success) {
        setSettings(response.data.data);
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings({
      ...settings,
      [key]: value
    });
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveMessage('');
    
    try {
      const response = await adminAPI.saveSettings(settings);
      
      if (response.data.success) {
        setSaveMessage('Настройки успешно сохранены!');
        setTimeout(() => setSaveMessage(''), 3000);
      } else {
        setSaveMessage('Ошибка при сохранении настроек');
      }
    } catch (error) {
      console.error('Ошибка сохранения настроек:', error);
      setSaveMessage('Ошибка при сохранении настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSettings(defaultSettings);
  };

  // Компонент для числовых полей настроек
  const SettingField = ({ label, settingKey, helperText, endAdornment, ...props }) => {
    const [localValue, setLocalValue] = React.useState('');
    const [isFocused, setIsFocused] = React.useState(false);
    
    React.useEffect(() => {
      if (!isFocused) {
        setLocalValue(formatSettingValue(settings[settingKey]));
      }
    }, [settings[settingKey], isFocused]);
    
    return (
      <TextField
        fullWidth
        label={label}
        type="text"
        value={isFocused ? localValue : formatSettingValue(settings[settingKey])}
        onChange={(e) => {
          const value = e.target.value;
          setLocalValue(value);
          // Не парсим сразу, просто сохраняем как есть
          // Парсинг произойдет при потере фокуса
        }}
        onFocus={() => {
          setIsFocused(true);
          setLocalValue(formatSettingValue(settings[settingKey]));
        }}
        onBlur={() => {
          setIsFocused(false);
          // Парсим и сохраняем значение при потере фокуса
          const parsed = parseSettingValue(localValue);
          if (parsed !== '' || localValue === '') {
            handleChange(settingKey, parsed);
          }
        }}
        helperText={helperText}
        InputProps={endAdornment ? {
          endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment>
        } : undefined}
        {...props}
      />
    );
  };

  // Компонент для переключателей
  const SettingSwitch = ({ label, settingKey, helperText }) => (
    <FormControlLabel
      control={
        <Switch
          checked={settings[settingKey] || false}
          onChange={(e) => handleChange(settingKey, e.target.checked)}
        />
      }
      label={
        <Box>
          <Typography>{label}</Typography>
          {helperText && <Typography variant="caption" color="text.secondary">{helperText}</Typography>}
        </Box>
      }
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Настройки игры
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleReset}
          >
            Сбросить
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={loading}
            sx={{ bgcolor: '#6366f1' }}
          >
            Сохранить изменения
          </Button>
        </Box>
      </Box>

      {saveMessage && (
        <Alert severity={saveMessage.includes('успешно') ? 'success' : 'error'} sx={{ mb: 2 }}>
          {saveMessage}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="CLAIM" />
          <Tab label="Стейкинг" />
          <Tab label="Депозиты и выводы" />
          <Tab label="Рефералы" />
          <Tab label="Общие" />
        </Tabs>

        {/* CLAIM настройки */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Настройки сбора накопленной прибыли
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                CLAIM собирает прибыль, накопленную от стейкинга. Например, если у пользователя депозит 1000 SPERM
                и за 24 часа набежало 10 SPERM прибыли, при нажатии CLAIM эти 10 SPERM переходят в копилку.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Кулдаун между CLAIM"
                settingKey="claim_cooldown_hours"
                helperText="Минимальное время между сборами прибыли"
                endAdornment="часов"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Время удержания кнопки"
                settingKey="claim_hold_seconds"
                helperText="Защита от случайных нажатий"
                endAdornment="сек"
              />
            </Grid>

          </Grid>
        </TabPanel>

        {/* Стейкинг настройки */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" sx={{ mb: 2 }}>Лунный стейкинг</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Начальный APR"
                settingKey="lunar_initial_apr"
                helperText="Процент в день при старте"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Рост APR в день"
                settingKey="lunar_daily_growth"
                helperText="На сколько растёт APR каждый день. Вводите через точку или запятую: 0.05 или 0,05 = +0.05% в день"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный депозит"
                settingKey="lunar_min_deposit"
                helperText="Минимальная сумма для стейкинга"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Burn при депозите"
                settingKey="lunar_burn_percent"
                helperText="Процент сжигания при создании депозита"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Визуальный множитель"
                settingKey="lunar_visual_multiplier"
                helperText="Множитель для отображения баланса (x2 = показывает в 2 раза больше)"
                endAdornment="x"
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                ⚠️ В лунном стейкинге вывод тела депозита невозможен - работает механика "вечной регрессии"
              </Alert>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" sx={{ mb: 2 }}>Управление стейкингами</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SettingSwitch
                label="🔒 Пул SPERM исчерпан (блок lunar/star)"
                settingKey="staking_pool_exhausted"
                helperText="ON — кнопки 'Создать' лунного и звёздного стейкинга станут серыми, бэкенд блокирует все способы создания (балансом, копилкой, USDT, TON). Марафон НЕ затрагивается."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Лунный стейкинг включен"
                settingKey="lunar_staking_enabled"
                helperText="Включить/выключить лунный стейкинг для пользователей"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Сообщение при выключенном лунном стейкинге"
                settingKey="lunar_staking_message"
                helperText="Что видят пользователи когда стейкинг выключен"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Звездный стейкинг включен"
                settingKey="star_staking_enabled"
                helperText="Включить/выключить звездный стейкинг для пользователей"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Сообщение при выключенном звездном стейкинге"
                settingKey="star_staking_message"
                helperText="Что видят пользователи когда стейкинг выключен"
              />
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 3 }} />
          
          <Typography variant="h6" sx={{ mb: 2 }}>Звездный стейкинг</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Начальный APR"
                settingKey="star_initial_apr"
                helperText="Процент в день при старте"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Рост APR в день"
                settingKey="star_daily_growth"
                helperText="APR растёт бесконечно без максимума"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный депозит"
                settingKey="star_min_deposit"
                helperText="Минимальная сумма для стейкинга"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Burn при депозите"
                settingKey="star_burn_percent"
                helperText="Процент сжигания при создании депозита"
                endAdornment="%"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Штрафы за вывод (по срокам)</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <SettingField
                label="До 30 дней"
                settingKey="star_penalty_30days"
                helperText="Штраф за вывод"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SettingField
                label="До 100 дней"
                settingKey="star_penalty_100days"
                helperText="Штраф за вывод"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SettingField
                label="До 200 дней"
                settingKey="star_penalty_200days"
                helperText="Штраф за вывод"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SettingField
                label="После 200 дней"
                settingKey="star_penalty_over200"
                helperText="Штраф за вывод"
                endAdornment="%"
              />
            </Grid>

            {/* 🌙 Лунный марафон */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, color: '#ffd700' }}>
                🌙 Лунный марафон (USDT)
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Марафон включен"
                settingKey="marathon_enabled"
                type="switch"
                helperText="Вкл/выкл Лунного марафона"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Сообщение"
                settingKey="marathon_message"
                helperText="Сообщение для пользователей"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный депозит"
                settingKey="marathon_min_deposit_usdt"
                helperText="Минимальная сумма для участия"
                endAdornment="USDT"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Длительность периода"
                settingKey="marathon_period_days"
                helperText="Количество дней в одном периоде"
                endAdornment="дней"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Всего периодов"
                settingKey="marathon_total_periods"
                helperText="Общее количество периодов марафона"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Дневной APR"
                settingKey="marathon_daily_apr"
                helperText="Процент дохода в день"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Общий ROI"
                settingKey="marathon_total_roi"
                helperText="Общий возврат за весь марафон"
                endAdornment="%"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Депозиты и выводы */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Настройки депозитов</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный депозит"
                settingKey="min_deposit_amount"
                helperText="Минимальная сумма пополнения в SPERM"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Максимальный депозит"
                settingKey="max_deposit_amount"
                helperText="Максимальная сумма пополнения в SPERM"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Текущий курс обмена (STON.fi)
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      {rateLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Typography variant="h6">
                          1 TON = {exchangeRate ? exchangeRate.toFixed(2) : '...'} SPERM
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {lastRateUpdate ? 
                        `Обновлено: ${lastRateUpdate.toLocaleTimeString()}` : 
                        'Загрузка...'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Refresh />}
                    onClick={loadExchangeRate}
                    disabled={rateLoading}
                  >
                    Обновить
                  </Button>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Адрес кошелька для депозитов"
                settingKey="deposit_wallet_address"
                helperText="TON адрес для приема платежей"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Настройки выводов</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальная сумма вывода"
                settingKey="min_withdrawal_amount"
                helperText="Минимальная сумма для вывода средств"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Комиссия за вывод"
                settingKey="withdrawal_commission"
                helperText="Процент комиссии при выводе (0 = без комиссии)"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Максимальный вывод в день"
                settingKey="max_daily_withdrawal"
                helperText="Лимит на вывод средств в сутки"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Время обработки вывода"
                settingKey="withdrawal_processing_time"
                helperText="Время обработки заявки на вывод"
                endAdornment="часов"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Автоматический вывод"
                settingKey="auto_withdrawal_enabled"
                helperText="Автоматическая обработка выводов без модерации"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Уведомления о выводах"
                settingKey="withdrawal_notifications"
                helperText="Отправлять админу уведомления о новых заявках"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный депозит для вывода"
                settingKey="min_deposit_for_withdrawal"
                helperText="Сколько нужно внести, чтобы разблокировать вывод"
                endAdornment="SPERM"
              />
            </Grid>
            
          </Grid>
        </TabPanel>

        {/* Реферальная система */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SettingSwitch
                label="Реферальная система включена"
                settingKey="referral_enabled"
                helperText="Разрешить пользователям приглашать друзей и получать награды"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Единовременные выплаты (% от первого депозита)</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 1"
                settingKey="ref_level1_deposit_percent"
                helperText="Процент от депозита прямого реферала"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 2"
                settingKey="ref_level2_deposit_percent"
                helperText="Процент от депозита реферала 2 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 3"
                settingKey="ref_level3_deposit_percent"
                helperText="Процент от депозита реферала 3 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 4"
                settingKey="ref_level4_deposit_percent"
                helperText="Процент от депозита реферала 4 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 5"
                settingKey="ref_level5_deposit_percent"
                helperText="Процент от депозита реферала 5 уровня"
                endAdornment="%"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Ежедневный процент от дохода рефералов</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 1"
                settingKey="ref_level1_daily_percent"
                helperText="Процент от ежедневного дохода прямого реферала"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 2"
                settingKey="ref_level2_daily_percent"
                helperText="Процент от ежедневного дохода реферала 2 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 3"
                settingKey="ref_level3_daily_percent"
                helperText="Процент от ежедневного дохода реферала 3 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 4"
                settingKey="ref_level4_daily_percent"
                helperText="Процент от ежедневного дохода реферала 4 уровня"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Уровень 5"
                settingKey="ref_level5_daily_percent"
                helperText="Процент от ежедневного дохода реферала 5 уровня"
                endAdornment="%"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Общие настройки */}
        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Режим работы</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Режим обслуживания"
                settingKey="maintenance_mode"
                helperText="Закрыть доступ к игре для обслуживания"
              />
            </Grid>
            <Grid item xs={12}>
              <SettingField
                label="Сообщение режима обслуживания"
                settingKey="maintenance_message"
                helperText="Текст плашки для пользователей во время тех. работ"
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Новые регистрации"
                settingKey="new_registrations"
                helperText="Разрешить регистрацию новых пользователей"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Лимиты и комиссии</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Минимальный вывод"
                settingKey="min_withdrawal"
                helperText="Минимальная сумма для вывода средств"
                endAdornment="SPERM"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Комиссия за вывод"
                settingKey="withdrawal_fee_percent"
                helperText="Процент комиссии при выводе"
                endAdornment="%"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Максимальный вывод в день"
                settingKey="max_withdrawal_per_day"
                helperText="Лимит на вывод средств в сутки"
                endAdornment="SPERM"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Безопасность</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Максимум попыток входа"
                settingKey="max_login_attempts"
                helperText="После превышения аккаунт блокируется"
                endAdornment="попыток"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Длительность блокировки"
                settingKey="lockout_duration_minutes"
                helperText="Время блокировки после превышения попыток"
                endAdornment="минут"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingField
                label="Длительность сессии"
                settingKey="session_duration_hours"
                helperText="Время жизни сессии пользователя"
                endAdornment="часов"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>Уведомления</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Telegram уведомления"
                settingKey="telegram_notifications"
                helperText="Отправлять уведомления через Telegram бота"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Email уведомления"
                settingKey="email_notifications"
                helperText="Отправлять уведомления на email"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SettingSwitch
                label="Push уведомления"
                settingKey="push_notifications"
                helperText="Отправлять push-уведомления в браузере"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default Settings;